import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { isDesktop } from "@/helpers/desktop";
import { listPrinters, printReceipt } from "@/helpers/native";
import { columnsForPageSize } from "@/routes/settings/config";
import useLocalStorage from "./local-storage";

export const usePrinters = () =>
	useQuery({
		queryFn: () => listPrinters(),
		queryKey: ["printers"],
		// The web build has no shell to ask, so do not retry into a guaranteed error.
		enabled: isDesktop(),
	});

/**
 * Characters per line for the currently selected printer. The receipt layout
 * needs it to size the product table's columns.
 */
export const useReceiptColumns = () => {
	const [printer] = useLocalStorage("printer", undefined);
	return columnsForPageSize(printer?.pageSize);
};

export const usePrint = () => {
	const { t } = useTranslation();
	const toastId = useRef<string>(null);
	const [printer] = useLocalStorage("printer", undefined);
	return useMutation({
		mutationFn: (lines: ReceiptLine[]) =>
			printReceipt(printer.name, {
				columns: columnsForPageSize(printer.pageSize),
				lines,
			}),
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onSuccess: () => {
			toast.success(t("settings.printSuccess"), {
				id: toastId?.current || undefined,
			});
		},
		onError: () => {
			toast.error(t("settings.printError"), {
				id: toastId?.current || undefined,
			});
		},
	});
};
